import { useState, useCallback, useEffect, memo } from 'react';
import { useLocation, Redirect } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { TopBar } from './TopBar';
import { getAuthState, getUIPrefs, updateUIPrefs } from '@services/storage';
import { TooltipProvider } from '@shared/ui';
import { LandingBackdrop } from '@shared/ui/brand';
import { brand } from '@shared/brand';
import { Sidebar } from './Sidebar';
import { APP_SIDEBAR_WIDTH } from '@shared/constants';
import { useHomePath, useMeAccess } from '@services/access';
import { LoadingScreen } from '@shared/ui/layout';
import { useViewportTier } from '@shared/hooks';
import type { UserRole } from '@shared/types';
import { EmailVerificationProvider } from '@modules/email-verification';
import { rememberReturnPath } from '../model/return-path';

interface AppLayoutProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
}

const MemoizedSidebar = memo(Sidebar);
const MemoizedTopBar = memo(brand.components.TopBar ?? TopBar);

export function AppLayout({ children, requiredRole }: AppLayoutProps) {
  const [location] = useLocation();
  const authState = getAuthState();
  const [collapsePref, setCollapsePref] = useState(() => getUIPrefs().sidebarCollapsed);
  const tier = useViewportTier();
  const isMobile = tier === 'mobile';
  // Tablet portrait starts collapsed; the toggle there is transient, not stored.
  const [tabletExpanded, setTabletExpanded] = useState(false);
  const sidebarCollapsed = tier === 'tabletPortrait' ? !tabletExpanded : collapsePref;
  const [mobileOpen, setMobileOpen] = useState(false);
  const access = useMeAccess();
  const home = useHomePath();
  // Member menu while loading, so the sidebar never flashes empty.
  const accessRole = access.isLoading ? 'member' : access.role;

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    if (tier === 'tabletPortrait') setTabletExpanded(false);
  }, [tier]);

  const handleToggleSidebar = useCallback(() => {
    if (tier === 'tabletPortrait') {
      setTabletExpanded((prev) => !prev);
      return;
    }
    setCollapsePref((prev) => {
      const next = !prev;
      updateUIPrefs({ sidebarCollapsed: next });
      return next;
    });
  }, [tier]);

  if (!authState.isAuthenticated) {
    // Idempotent write, so a re-render before the redirect lands is harmless.
    rememberReturnPath(`${window.location.pathname}${window.location.search}`);
    return <Redirect to="/auth/sign-in" />;
  }

  if (requiredRole && authState.role !== requiredRole) {
    if (!home) return <LoadingScreen />;
    // Already on home and not allowed here: render nothing, do not show the page.
    if (location !== home) return <Redirect to={home} />;
    return null;
  }

  // Matches the sidebar's width; the mobile drawer overlays, so content reserves nothing.
  const sidebarWidth = isMobile
    ? 0
    : sidebarCollapsed
      ? APP_SIDEBAR_WIDTH.collapsed
      : APP_SIDEBAR_WIDTH.expanded;

  return (
    <TooltipProvider delayDuration={200}>
      <EmailVerificationProvider>
      {/* `clip`, not `hidden`: hidden makes this a scroll container and breaks every sticky child. */}
      <div
        data-app-shell="landing"
        className="relative min-h-screen overflow-x-clip bg-landing-page text-landing-ink font-sans antialiased selection:bg-tone-selection/40 selection:text-tone-on-selection"
      >
        <LandingBackdrop />
        <AnimatePresence>
          {isMobile && mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 z-40 bg-landing-ink/10 backdrop-blur-sm"
            />
          )}
        </AnimatePresence>

        <MemoizedSidebar
          accessRole={accessRole}
          collapsed={sidebarCollapsed}
          onToggle={handleToggleSidebar}
          isMobile={isMobile}
          mobileOpen={mobileOpen}
          onCloseMobile={() => setMobileOpen(false)}
        />
        {/* The offset snaps: animating marginLeft would relayout the whole content each frame. */}
        <div
          className="min-h-screen flex flex-col"
          style={{ marginLeft: sidebarWidth, width: isMobile ? '100%' : undefined }}
        >
          <MemoizedTopBar onOpenMobile={() => setMobileOpen(true)} />
          <main className="relative flex-1 min-h-0" data-testid="main-content">
            <AnimatePresence mode="wait">
              <motion.div
                key={location}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
      </EmailVerificationProvider>
    </TooltipProvider>
  );
}

interface AuthLayoutProps {
  children: React.ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const authState = getAuthState();
  const [location] = useLocation();
  const home = useHomePath();

  if (authState.isAuthenticated && !location.includes('/auth/')) {
    if (!home) return <LoadingScreen />;
    return <Redirect to={home} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/20 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        className="w-full max-w-md"
      >
        {children}
      </motion.div>
    </div>
  );
}
